import get from 'lodash/get';
import isArray from 'lodash/isArray';

/**
 * Provides effecient utiltiy methods for handling extension collections
 */
class ExtensionCollection {
  static initFromBackboneElement(
    backboneElement: fhir.BackboneElement,
  ): ExtensionCollection {
    const modifierExtensions = get<
      fhir.BackboneElement,
      'modifierExtension',
      fhir.Extension[]
    >(backboneElement, 'modifierExtension', []);
    const extensions = get<fhir.BackboneElement, 'extension', fhir.Extension[]>(
      backboneElement,
      'extension',
      [],
    );

    const combined: fhir.Extension[] = [];
    if (isArray(modifierExtensions) && modifierExtensions.length) {
      combined.push(...modifierExtensions);
    }

    if (isArray(extensions) && extensions.length) {
      combined.push(...extensions);
    }

    return new ExtensionCollection(combined);
  }

  _systemUrlHash: { [url: string]: fhir.Extension };

  constructor(extensions: fhir.Extension[] = [] as fhir.Extension[]) {
    this._systemUrlHash = {};

    extensions.filter(Boolean).forEach(extension => {
      if (this._systemUrlHash[extension.url]) {
        console.error(
          `Surveys does not support duplicate extensions with the same url.  Ignoring second extension with url: "${extension.url}"`,
        );
      } else {
        this._systemUrlHash[extension.url] = extension;
      }
    });
  }

  getValueString(url: string): string {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueString', string>(
      targetExtension,
      'valueString',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueUri(url: string): string {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueUri', string>(
      targetExtension,
      'valueUri',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueInteger(url: string): number {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueInteger', number>(
      targetExtension,
      'valueInteger',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueBoolean(url: string): boolean {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueBoolean', boolean>(
      targetExtension,
      'valueBoolean',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueQuantity(url: string): fhir.Quantity {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueQuantity', fhir.Quantity>(
      targetExtension,
      'valueQuantity',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueDecimal(url: string): number {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueDecimal', number>(
      targetExtension,
      'valueDecimal',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueMarkdown(url: string): string {
    const targetExtension = this._systemUrlHash[url];
    return get<fhir.Extension, 'valueMarkdown', string>(
      targetExtension,
      'valueMarkdown',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getValueCodeableConcept(url: string): fhir.Coding[] {
    const targetExtension = this._systemUrlHash[url];
    const valueCodeableConcept = get<
      fhir.Extension,
      'valueCodeableConcept',
      fhir.CodeableConcept
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    >(targetExtension, 'valueCodeableConcept', null);
    return valueCodeableConcept?.coding || [];
  }

  get extensions(): fhir.Extension[] {
    return Object.values(this._systemUrlHash);
  }

  upsertExtension(newExtension: fhir.Extension): void {
    if (newExtension?.url) {
      this._systemUrlHash[newExtension.url] = newExtension;
    }
  }

  /**
   * Determines if the extension with the target url extists in the collection
   * @param extensionUrl
   */
  hasExtension(extensionUrl: string): boolean {
    return !!this._systemUrlHash[extensionUrl];
  }
}

export default ExtensionCollection;
